import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { ClipboardLogo } from "@src/appV2/lib";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { type ReactElement } from "react";

import { useImpersonateAuth } from "./useImpersonateAuth";

export function ImpersonatePage(): ReactElement {
  const {
    state,
    statusMessage,
    errorMessage,
    usernameInput,
    tokenInput,
    usernameFromUrl,
    setUsernameInput,
    setTokenInput,
    startManualAuth,
    setState,
  } = useImpersonateAuth();

  return (
    <ThemeProvider theme={getTheme()}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          paddingY: 4,
          paddingX: 6,
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Container maxWidth="sm">
            <Stack spacing={4} alignItems="center" textAlign="center">
              <ClipboardLogo width={200} />

              {(state === "loading" || state === "waiting") && (
                <>
                  <CircularProgress />
                  <Text variant="h5">
                    {state === "waiting"
                      ? "Waiting for employee token..."
                      : "Authenticating as impersonator..."}
                  </Text>
                  {statusMessage && (
                    <Text variant="body2" color="text.secondary">
                      {statusMessage}
                    </Text>
                  )}
                </>
              )}

              {state === "manual-input" && (
                <>
                  <Text variant="h5">Impersonation</Text>
                  {usernameFromUrl ? (
                    <Text>
                      Paste the employee ID token below to impersonate worker{" "}
                      <strong>{usernameFromUrl}</strong>.
                    </Text>
                  ) : (
                    <>
                      <Text>
                        Enter the Cognito username and employee ID token to start impersonation.
                      </Text>
                      <TextField
                        fullWidth
                        label="Cognito Username"
                        value={usernameInput}
                        onChange={(event) => {
                          setUsernameInput(event.target.value);
                        }}
                      />
                    </>
                  )}
                  <TextField
                    fullWidth
                    multiline
                    label="Employee ID Token"
                    minRows={3}
                    value={tokenInput}
                    onChange={(event) => {
                      setTokenInput(event.target.value);
                    }}
                  />
                  <Button
                    variant="contained"
                    size="large"
                    disabled={!tokenInput.trim() || (!usernameFromUrl && !usernameInput.trim())}
                    onClick={() => {
                      const username = usernameFromUrl || usernameInput.trim();
                      startManualAuth(username, tokenInput.trim());
                    }}
                  >
                    Impersonate
                  </Button>
                </>
              )}

              {state === "success" && <Text variant="h5">Success! Redirecting...</Text>}

              {state === "error" && (
                <>
                  <Text variant="h5" color="error">
                    Impersonation Failed
                  </Text>
                  <Text>{errorMessage}</Text>
                  <Button
                    variant="contained"
                    sx={{ marginTop: 2 }}
                    onClick={() => {
                      setState("manual-input");
                    }}
                  >
                    Try Again
                  </Button>
                </>
              )}

              {state === "session-ended" && (
                <Text variant="h5">Impersonation session has ended.</Text>
              )}
            </Stack>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
